import env, { isLocalDeployment } from '@frontend/env';
import { getOpenTabCount } from '@frontend/tab-management';
import { getSentryConfig as getBaseSentryConfig, SENTRY_SESSION_STORAGE_KEYS } from '@frontend/tracking';

/*This is a list of regular expressions matching the error messages we wish to ignore from sentry exceptions capturing.
Please add here all error messages that do not affect the normal operation of the app.*/
const ignorePatterns: (RegExp | string)[] = [
  /status code 40[134]/gim,
  /The Stripe Terminal ConnectionToken has already been redeemed./gi,
  /Cannot find a descendant at path/gim,
  /Cannot resolve a DOM point from Slate point/gim,
  /Location-Id not configured/gi,
  /Cannot get the leaf node at paths/gi,
  /invalid \| token is expired by \d+h\d+m\d+s/gi,
  /expired \| token is expired by \d+h\d+m\d+s/gi,
  'AbortError: The play() request was interrupted by end of playback.',
  'AbortError: The play() request was interrupted by a new load request.',
  "NotAllowedError: play() failed because the user didn't interact with the document first.",
  'Non-Error exception captured',
  'Non-Error promise rejection captured',
  'Event `Event` (type=error) captured as promise rejection',
  'onDrag called before onDragStart.',
  'onDragEnd called before onDragStart.',
  /Cannot assign to read only property 'x' of object.*/,
  /FirebaseError: Missing or insufficient permissions.*/,
  /FirebaseError: The datastore operation timed out, or the data was temporarily unavailable.*/,
];

/* This is an array of regular expressions matching the endpoints we wish to ignore.
If any endpoint returns errors that do not affect the application's operation, you should add it here. */
const ignoreEndpoints: RegExp[] = [/person_extended/i, /stripe\.com/i, /pendo\.io/i, /static\.zdassets\.com/i];
type Environment = 'test' | 'preview' | 'production' | 'debug';

export const WEAVE_SENTRY_SESSION_STORAGE_KEYS = {
  ...SENTRY_SESSION_STORAGE_KEYS,
  localEnable: 'sentryEnableLocal',
  environment: 'sentryEnvironment',
} as const;

export const {
  localEnable: SENTRY_LOCAL_ENABLE_KEY,
  environment: SENTRY_ENVIRONMENT_KEY,
  errorSampleRate: SENTRY_ERROR_SAMPLE_RATE_KEY,
  debug: SENTRY_DEBUG_KEY,
} = WEAVE_SENTRY_SESSION_STORAGE_KEYS;

const ENVIRONMENT_OVERRIDES: Environment[] = ['test', 'preview', 'production', 'debug'];

const getSessionStorageItem = (key: string): string | null => {
  try {
    if (typeof globalThis === 'undefined' || !('sessionStorage' in globalThis)) {
      return null;
    }

    return globalThis.sessionStorage?.getItem(key) ?? null;
  } catch {
    return null;
  }
};

const setSessionStorageItem = (key: string, value: string) => {
  try {
    if (typeof globalThis === 'undefined' || !('sessionStorage' in globalThis)) {
      return;
    }

    globalThis.sessionStorage?.setItem(key, value);
  } catch {
    // ignore storage write failures in local validation helpers
  }
};

const removeSessionStorageItem = (key: string) => {
  try {
    if (typeof globalThis === 'undefined' || !('sessionStorage' in globalThis)) {
      return;
    }

    globalThis.sessionStorage?.removeItem(key);
  } catch {
    // ignore storage removal failures in local validation helpers
  }
};

const reloadWindow = () => {
  try {
    globalThis.location?.reload();
  } catch {
    // ignore reload failures in tests/non-browser environments
  }
};

export const enableLocalDebugSentry = ({
  environment = 'debug',
  errorSampleRate = 1,
  reload = true,
}: {
  environment?: Environment;
  errorSampleRate?: number;
  reload?: boolean;
} = {}) => {
  setSessionStorageItem(SENTRY_LOCAL_ENABLE_KEY, 'true');
  setSessionStorageItem(SENTRY_ENVIRONMENT_KEY, environment);
  setSessionStorageItem(SENTRY_ERROR_SAMPLE_RATE_KEY, String(errorSampleRate));
  setSessionStorageItem(SENTRY_DEBUG_KEY, 'true');

  if (reload) {
    reloadWindow();
  }
};

export const disableLocalDebugSentry = ({ reload = true }: { reload?: boolean } = {}) => {
  removeSessionStorageItem(SENTRY_LOCAL_ENABLE_KEY);
  removeSessionStorageItem(SENTRY_ENVIRONMENT_KEY);
  removeSessionStorageItem(SENTRY_ERROR_SAMPLE_RATE_KEY);
  removeSessionStorageItem(SENTRY_DEBUG_KEY);

  if (reload) {
    reloadWindow();
  }
};

export const shouldInitializeSentry = () => {
  const localEnableOverride = getSessionStorageItem(SENTRY_LOCAL_ENABLE_KEY);

  if (localEnableOverride === 'true') {
    return true;
  }

  return import.meta.env.MODE === 'production' && !isLocalDeployment();
};

export const getSentryEnvironment = (): Environment => {
  const override = getSessionStorageItem(SENTRY_ENVIRONMENT_KEY);

  if (override && ENVIRONMENT_OVERRIDES.includes(override as Environment)) {
    return override as Environment;
  }

  if (env.IS_PREVIEW === 'true') {
    return 'preview';
  }

  if (import.meta.env.MODE === 'production') {
    return 'production';
  }

  return 'test';
};

/**
 * We initialize Sentry with the DSN (which is the URL of the Sentry server) and the release (which is
 * the version of the app that is running).
 *
 * Weave-specific env var names differ from the base defaults, so we pass them explicitly.
 * All other config (integrations, sampling, filtering, transport) inherits from the base.
 */
export const getSentryConfig = () => {
  const environment = getSentryEnvironment();

  return getBaseSentryConfig({
    // IMPORTANT: These env vars must match what is set in reusable-build-and-deploy.yml
    dsn: env.SENTRY_DSN,
    release: env.VERSION,
    environment,
    ignoreErrors: ignorePatterns,
    ignoreTransactions: ignorePatterns,
    denyUrls: ignoreEndpoints,
    extraTags: () => ({ openTabCount: String(getOpenTabCount()) }),
  });
};
